import { useState } from "react";
import SEO from "@/components/SEO";
import { COLD_START_HINT_MS } from "@/config/api";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { postAuthRedirectFor } from "@/utils/postAuthRedirect";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Bot, ArrowRight, Loader2, Check, X } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import AppleSignInButton from "@/components/AppleSignInButton";
import { isNative } from "@/utils/capacitor";
import { trackSignup } from "@/lib/analytics";

const PasswordRequirement = ({ met, label }) => (
  <li
    className={`flex items-center gap-1.5 ${
      met ? "text-green-400" : "text-slate-400"
    }`}
  >
    {met ? (
      <Check className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
    ) : (
      <X className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
    )}
    <span>{label}</span>
  </li>
);

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingPlan = searchParams.get("plan");
  const pendingBilling = searchParams.get("billing") || "monthly";
  const referralCode = searchParams.get("ref");
  // Intent comes from the landing-page hero chips + kits showcase
  // (PR #58). Resolves to a dashboard path the user will land on
  // post-signup, preserving the discovery → action funnel.
  const pendingIntent = searchParams.get("intent");
  const { register } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  // Cold-start reassurance: Render Free spins the backend down after 15min
  // idle, so a new visitor's very first request (this signup) can hang 30-50s
  // on a plain spinner and read as "broken" → bounce. After ~7s we show a
  // friendly "server waking up" line so they wait instead of leaving.
  const [slowHint, setSlowHint] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  // Mirror backend/routers/auth.py rules exactly (≥8 chars + ≥1 digit) so
  // the frontend doesn't let a submittable-looking password through only
  // for the backend to reject it with a 400.
  const passwordChecks = {
    length: formData.password.length >= 8,
    digit: /\d/.test(formData.password),
    match:
      formData.confirmPassword.length > 0 &&
      formData.password === formData.confirmPassword,
  };
  const passwordValid =
    passwordChecks.length && passwordChecks.digit && passwordChecks.match;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!passwordChecks.match) {
      toast.error(t("auth.register.errorPassword"));
      return;
    }

    if (!passwordChecks.length || !passwordChecks.digit) {
      toast.error(t("auth.register.errorPasswordLength"));
      return;
    }

    setLoading(true);
    // Show the cold-start reassurance only if the request is still pending
    // after 7s (a warm backend answers in <1s, so it never fires then).
    const slowTimer = setTimeout(() => setSlowHint(true), COLD_START_HINT_MS);

    try {
      await register(
        formData.email,
        formData.password,
        formData.name,
        referralCode
      );
      toast.success(t("auth.register.success"));
      // GA4 `sign_up` key event — the primary ad-conversion signal. Without
      // this, paid campaigns can't count signups (see ADS_ASSETS.md §5).
      trackSignup(referralCode ? "referral" : "email");
      // Routing priority: a chosen paid plan wins over an intent
      // (the user committed to billing first), then intent-based
      // routing closes the discovery loop from the hero chips, then
      // /dashboard is the default landing.
      if (pendingPlan && pendingPlan !== "free") {
        navigate(
          `/pricing?autoselect=${pendingPlan}&billing=${pendingBilling}`
        );
      } else {
        const intentTarget = postAuthRedirectFor(pendingIntent);
        navigate(intentTarget ?? "/dashboard");
      }
    } catch (error) {
      console.error("Register error:", error);
      const message =
        error.response?.data?.detail ||
        t("auth.register.error", "Erreur lors de l'inscription");
      toast.error(message);
    } finally {
      clearTimeout(slowTimer);
      setSlowHint(false);
      setLoading(false);
    }
  };

  const PLAN_BENEFITS = {
    free: {
      title: t("auth.register.benefits.free", "Commencez gratuitement"),
      features: [
        t("auth.register.benefits.messages", "50 messages gratuits par mois"),
        t("auth.register.benefits.documents", "Upload de 2 documents"),
        t("auth.register.benefits.history", "Historique des conversations"),
        t("auth.register.benefits.assistants", "Assistants personnalisés"),
      ],
    },
    starter: {
      title: t("auth.register.benefits.starter", "Passez au plan Starter"),
      features: [
        "500 messages par mois",
        "10 documents",
        "AI Writing Tools",
        "Support email",
        t("auth.register.benefits.trial", "7 jours d'essai gratuit"),
      ],
    },
    pro: {
      title: t("auth.register.benefits.pro", "Passez au plan Pro"),
      features: [
        "2 000 messages",
        t("auth.register.benefits.unlimitedDocs", "Documents illimités"),
        "AI Tools",
        t("auth.register.benefits.images", "Génération d'images"),
        t("auth.register.benefits.trial", "7 jours d'essai gratuit"),
      ],
    },
    business: {
      title: t("auth.register.benefits.business", "Passez au plan Business"),
      features: [
        t("auth.register.benefits.unlimitedMessages", "Messages illimités"),
        t("auth.register.benefits.unlimitedDocs", "Documents illimités"),
        "API Access",
        "Analytics",
        t("auth.register.benefits.trial", "7 jours d'essai gratuit"),
      ],
    },
    enterprise: {
      title: t(
        "auth.register.benefits.enterprise",
        "Passez au plan Enterprise"
      ),
      features: [
        t("auth.register.benefits.everything", "Tout illimité"),
        "SLA",
        "Onboarding",
        "Account manager",
        t("auth.register.benefits.trial", "7 jours d'essai gratuit"),
      ],
    },
  };
  const planInfo = PLAN_BENEFITS[pendingPlan] || PLAN_BENEFITS.free;
  const benefits = planInfo.features;

  return (
    <div className="min-h-screen bg-background flex">
      <SEO
        title={t("auth.register.title")}
        description={t("auth.register.subtitle")}
        canonical="/register"
      />
      {/* Left side - Visual */}
      {/* Saturated brand panel with white content — legible in BOTH themes
          (the old translucent from-accent/20 washed out to pale under light). */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-gradient-to-br from-accent to-primary-strong relative overflow-hidden">
        <div className="relative z-10 p-12 max-w-lg">
          <div className="w-24 h-24 rounded-3xl bg-white/15 backdrop-blur-xl border border-white/25 flex items-center justify-center mb-8">
            <Bot className="w-12 h-12 text-white" />
          </div>
          <h2 className="font-heading text-3xl font-bold text-white mb-4">
            {planInfo.title}
          </h2>
          <p className="text-white/85 mb-8">{t("auth.register.subtitle")}</p>

          <ul className="space-y-4">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-center gap-3 text-white">
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-white" />
                </div>
                {benefit}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 mb-8"
            data-testid="register-logo"
          >
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-white">
              Stronger Bot
            </span>
          </Link>

          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            {t("auth.register.title")}
          </h1>
          <p className="text-slate-400 mb-8">{t("auth.register.subtitle")}</p>

          {/* OAuth providers — placed above the email/password form
              because they're the faster path (1 click vs 4 fields).
              Order matches LoginPage: Google first, Apple second. */}
          {/* Hidden in the native app — Google/Apple block OAuth in embedded
              WebViews; native users use email/password (§2 for native plugins). */}
          {!isNative && (
            <>
              <div className="space-y-3 mb-6" data-testid="oauth-providers">
                <GoogleSignInButton text="signup_with" />
                <AppleSignInButton mode="signup" />
              </div>

              <div className="relative mb-6">
                <div
                  className="absolute inset-0 flex items-center"
                  aria-hidden="true"
                >
                  <div className="w-full border-t border-white/10" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-background px-4 text-slate-400">
                    {t("auth.login.orEmail", "ou avec votre email")}
                  </span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-300">
                {t("auth.register.name")}
              </Label>
              <Input
                id="name"
                type="text"
                placeholder={t("auth.register.namePlaceholder", "Votre nom")}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                className="bg-white/5 border-white/10 focus:border-primary/50 text-white placeholder:text-slate-600"
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">
                {t("auth.register.email")}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
                className="bg-white/5 border-white/10 focus:border-primary/50 text-white placeholder:text-slate-600"
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">
                {t("auth.register.password")}
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                required
                aria-describedby="password-requirements"
                className="bg-white/5 border-white/10 focus:border-primary/50 text-white placeholder:text-slate-600"
                data-testid="register-password"
              />
              <ul
                id="password-requirements"
                aria-live="polite"
                className="text-xs space-y-1 mt-1"
                data-testid="password-requirements"
              >
                <PasswordRequirement
                  met={passwordChecks.length}
                  label={t(
                    "auth.register.pwdMinLength",
                    "8 caractères minimum"
                  )}
                />
                <PasswordRequirement
                  met={passwordChecks.digit}
                  label={t("auth.register.pwdDigit", "Au moins un chiffre")}
                />
              </ul>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-300">
                {t("auth.register.confirmPassword")}
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={(e) =>
                  setFormData({ ...formData, confirmPassword: e.target.value })
                }
                required
                aria-describedby="password-match-status"
                className="bg-white/5 border-white/10 focus:border-primary/50 text-white placeholder:text-slate-600"
                data-testid="register-confirm-password"
              />
              {formData.confirmPassword.length > 0 && (
                <p
                  id="password-match-status"
                  aria-live="polite"
                  className="text-xs mt-1"
                  data-testid="password-match-status"
                >
                  <PasswordRequirement
                    met={passwordChecks.match}
                    label={
                      passwordChecks.match
                        ? t(
                            "auth.register.pwdMatch",
                            "Les mots de passe correspondent"
                          )
                        : t(
                            "auth.register.pwdNoMatch",
                            "Les mots de passe ne correspondent pas"
                          )
                    }
                  />
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full btn-glow bg-primary-strong hover:bg-primary-strong/90"
              disabled={loading || !passwordValid}
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("auth.register.loading")}
                </>
              ) : (
                <>
                  {t("auth.register.submit")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            {loading && slowHint && (
              <p
                className="text-center text-sm text-slate-400 mt-3 animate-in-up"
                role="status"
                data-testid="register-slow-hint"
              >
                {t(
                  "auth.register.slowServer",
                  "Premier démarrage du serveur — cela peut prendre jusqu'à ~40 secondes. Merci de patienter, ne recharge pas la page."
                )}
              </p>
            )}
          </form>

          <p className="text-center text-slate-400 mt-8">
            {t("auth.register.hasAccount")}{" "}
            <Link
              to="/login"
              className="text-primary hover:text-primary/80 font-medium"
              data-testid="login-link"
            >
              {t("auth.register.login")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
